import React, { useState, useEffect } from "react";
import request from "../../../Helpers/API";
import Swal from "sweetalert2";
import { useDiasSeleccionados } from "./Helper/useDiasSeleccionados";
import { RSMiniCalendario } from "./RSMiniCalendario";
import { RSAvatarPicker } from "./RSAvatarPicker";

const RESOLUCIONES_POR_FORMATO = {
    "9:16": { width: 1080, height: 1920 },
    "1:1": { width: 1080, height: 1080 },
    "16:9": { width: 1920, height: 1080 }
};

export const RSReelsModal = ({ open, onClose, onActualizar, eventoSeleccionado, modoInicial }) => {
    const esEdicion = !!eventoSeleccionado;

    const [modo, setModo] = useState(modoInicial ?? "Automatico");
    const [tema, setTema] = useState("");
    const [informacionEmpresa, setInformacionEmpresa] = useState("");
    const [escenas, setEscenas] = useState([]);
    const [avatares, setAvatares] = useState([]);
    const [aspectRatio, setAspectRatio] = useState("9:16");
    const [hora, setHora] = useState("");
    const [redSocial, setRedSocial] = useState([]);
    const [cargar, setCargar] = useState(true);
    const [guardando, setGuardando] = useState(false);
    const diasPub = useDiasSeleccionados();

    const toggleRedSocial = (red) => {
        setRedSocial(prev =>
            prev.includes(red)
                ? prev.filter(r => r !== red)
                : [...prev, red]
        );
    };

    const agregarEscena = () => {
        setEscenas(prev => [...prev, { numeroEscena: prev.length + 1, avatarId: "", texto: "" }]);
    };

    const eliminarEscena = (indice) => {
        setEscenas(prev =>
            prev.filter((_, i) => i !== indice).map((e, i) => ({ ...e, numeroEscena: i + 1 }))
        );
    };

    const actualizarEscena = (indice, campo, valor) => {
        setEscenas(prev => prev.map((e, i) => i === indice ? { ...e, [campo]: valor } : e));
    };

    useEffect(() => {
        if (!open) return;

        request
            .get("Avatares/Listado")
            .then((result) => setAvatares(result ?? []))
            .catch(() => setAvatares([]));

        if (esEdicion) {
            setCargar(eventoSeleccionado.cargar === true || eventoSeleccionado.cargar === 1);
            setModo(eventoSeleccionado.modo ?? "Automatico");
            setTema(eventoSeleccionado.tema ?? "");
            setInformacionEmpresa(eventoSeleccionado.informacion_Empresa ?? "");
            setAspectRatio(eventoSeleccionado.aspect_Ratio ?? "9:16");
            setHora(eventoSeleccionado.hora ?? "");
            setRedSocial(
                eventoSeleccionado.red_Social
                    ? eventoSeleccionado.red_Social.split(",").map(x => x.trim())
                    : []
            );

            request
                .get(`Reels_Publicaciones_Detalle/Listado?folio=${eventoSeleccionado.folio}`)
                .then((detalle) => {
                    const dias = (detalle ?? []).map(d => {
                        const fecha = new Date(d.fecha_Envio);
                        return { day: fecha.getDate(), month: fecha.getMonth(), year: fecha.getFullYear(), consecutivo: d.consecutivo };
                    });
                    diasPub.hidratar(dias);
                })
                .catch(() => {});

            if (eventoSeleccionado.modo === "Manual") {
                request
                    .get(`Reels_Escenas/Listado?folio=${eventoSeleccionado.folio}`)
                    .then((result) => {
                        const escenasCargadas = (result ?? []).map(e => ({
                            numeroEscena: e.numero_Escena,
                            avatarId: e.avatar_Id,
                            texto: e.texto
                        }));
                        setEscenas(escenasCargadas);
                    })
                    .catch(() => {});
            }
        } else {
            setModo(modoInicial ?? "Automatico");
        }
    }, [open]);

    const onLimpiar = () => {
        setModo("Automatico");
        setTema("");
        setInformacionEmpresa("");
        setEscenas([]);
        setAspectRatio("9:16");
        setHora("");
        setRedSocial([]);
        setCargar(true);
        diasPub.reiniciar();
    };

    const onCerrar = () => {
        onLimpiar();
        onClose();
    };

    const onGuardar = async () => {
        if (modo === "Automatico" && !tema) return Swal.fire("REDES SOCIALES", "Ingrese el tema del reel", "warning");
        if (modo === "Manual" && escenas.length === 0) return Swal.fire("REDES SOCIALES", "Agregue al menos una escena", "warning");
        if (modo === "Manual" && escenas.some(e => !e.avatarId || !e.texto))
            return Swal.fire("REDES SOCIALES", "Seleccione el avatar y escriba el texto de cada escena", "warning");
        if (diasPub.diasSeleccionados.length === 0) return Swal.fire("REDES SOCIALES", "Seleccione al menos un día de publicación", "warning");
        if (redSocial.length === 0) return Swal.fire("REDES SOCIALES", "Seleccione al menos una red social", "warning");

        setGuardando(true);
        try {
            const resolucion = RESOLUCIONES_POR_FORMATO[aspectRatio] ?? RESOLUCIONES_POR_FORMATO["9:16"];

            const datosEncabezado = {
                Modo: modo,
                Tema: modo === "Automatico" ? tema : (tema || null),
                Informacion_Empresa: informacionEmpresa || null,
                Aspect_Ratio: aspectRatio,
                Width: resolucion.width,
                Height: resolucion.height,
                Hora: hora || null,
                Red_Social: redSocial.length > 0 ? redSocial.join(", ") : null,
                Cargar: cargar
            };

            let folio;
            if (esEdicion) {
                folio = eventoSeleccionado.folio;
                await request.post("Reels_Encabezado/Editar", { Folio: folio, ...datosEncabezado });

                const diasEliminar = diasPub.diasOriginales.filter(orig =>
                    !diasPub.diasSeleccionados.some(d => d.day === orig.day && d.month === orig.month && d.year === orig.year)
                );
                const diasAgregar = diasPub.diasSeleccionados.filter(sel =>
                    !diasPub.diasOriginales.some(d => d.day === sel.day && d.month === sel.month && d.year === sel.year)
                );

                for (const dia of diasEliminar) {
                    await request.post("Reels_Publicaciones_Detalle/Eliminar", {
                        Folio: folio, folio: folio,
                        Consecutivo: dia.consecutivo, consecutivo: dia.consecutivo
                    });
                }
                for (const dia of diasAgregar) {
                    const fecha = new Date(dia.year, dia.month, dia.day);
                    await request.post("Reels_Publicaciones_Detalle/Guardar", {
                        Folio: folio, folio: folio,
                        Fecha_Envio: fecha.toISOString(), fecha_Envio: fecha.toISOString(),
                        Estatus: "Pendiente", estatus: "Pendiente"
                    });
                }

                if (modo === "Manual") {
                    await request.post("Reels_Escenas/EliminarTodas", { Folio: folio });
                    for (const escena of escenas) {
                        await request.post("Reels_Escenas/Guardar", {
                            Folio: folio,
                            Numero_Escena: escena.numeroEscena,
                            Avatar_Id: escena.avatarId,
                            Texto: escena.texto
                        });
                    }
                }
            } else {
                const resultEnc = await request.post("Reels_Encabezado/Guardar", datosEncabezado);
                folio = resultEnc.folio || resultEnc.Folio;

                for (const dia of diasPub.diasSeleccionados) {
                    const fecha = new Date(dia.year, dia.month, dia.day);
                    await request.post("Reels_Publicaciones_Detalle/Guardar", {
                        Folio: folio, folio: folio,
                        Fecha_Envio: fecha.toISOString(), fecha_Envio: fecha.toISOString(),
                        Estatus: "Pendiente", estatus: "Pendiente"
                    });
                }

                if (modo === "Manual") {
                    for (const escena of escenas) {
                        await request.post("Reels_Escenas/Guardar", {
                            Folio: folio,
                            Numero_Escena: escena.numeroEscena,
                            Avatar_Id: escena.avatarId,
                            Texto: escena.texto
                        });
                    }
                }
            }

            await Swal.fire("REDES SOCIALES", esEdicion ? "Reel editado correctamente" : "Reel guardado correctamente", "success");
            onActualizar();
            onCerrar();
        } catch (error) {
            Swal.fire("REDES SOCIALES", JSON.stringify(error), "error");
        } finally {
            setGuardando(false);
        }
    };

    const onEliminar = async () => {
        const confirm = await Swal.fire({
            title: "¿Eliminar reel?",
            text: "Esta acción no se puede deshacer.",
            icon: "warning",
            showCancelButton: true,
            confirmButtonColor: "#d33",
            cancelButtonColor: "#3085d6",
            confirmButtonText: "Sí, eliminar",
            cancelButtonText: "Cancelar"
        });

        if (!confirm.isConfirmed) return;

        setGuardando(true);
        try {
            await request.post("Reels_Encabezado/Eliminar", eventoSeleccionado.folio);
            await Swal.fire("REDES SOCIALES", "Eliminado correctamente", "success");
            onActualizar();
            onCerrar();
        } catch (error) {
            Swal.fire("REDES SOCIALES", JSON.stringify(error), "error");
        } finally {
            setGuardando(false);
        }
    };

    if (!open) return null;

    return (
        <div className="rs-modal-overlay">
            <div className="rs-modal" onClick={e => e.stopPropagation()}>

                <div className="rs-modal__header">
                    <span className="rs-modal__title">{esEdicion ? `Editar Reel — ${eventoSeleccionado.folio}` : "Nuevo Reel"}</span>
                    <button className="rs-modal__close" onClick={onCerrar}>✕</button>
                </div>

                <div className="rs-modal__body">

                    <div className="rs-modo-badge">
                        Modo: <strong>{modo === "Manual" ? "Manual" : "Automático"}</strong>
                    </div>

                    {modo === "Automatico" && (
                        <>
                            <div className="rs-field">
                                <label className="rs-field__label">Tema <span className="rs-field__hint">(la IA genera el guion y elige el avatar)</span></label>
                                <textarea
                                    className="rs-textarea"
                                    rows={3}
                                    placeholder="Ej: Presentación de nuestro asistente virtual de maquinaria"
                                    value={tema}
                                    onChange={e => setTema(e.target.value)}
                                />
                            </div>
                            <div className="rs-field">
                                <label className="rs-field__label">Información de la empresa <span className="rs-field__hint">(opcional, reemplaza el texto por defecto)</span></label>
                                <textarea
                                    className="rs-textarea"
                                    rows={2}
                                    placeholder="Déjalo vacío para usar el texto por defecto"
                                    value={informacionEmpresa}
                                    onChange={e => setInformacionEmpresa(e.target.value)}
                                />
                            </div>
                        </>
                    )}

                    {modo === "Manual" && (
                        <div className="rs-field">
                            <label className="rs-field__label">Título <span className="rs-field__hint">(opcional, solo para identificarlo)</span></label>
                            <input className="rs-input" type="text" placeholder="Ej: Presentación taller de servicio" value={tema} onChange={e => setTema(e.target.value)} />

                            <div className="rs-escenas">
                                {escenas.map((escena, indice) => (
                                    <div key={indice} className="rs-escena">
                                        <div className="rs-escena__header">
                                            <span className="rs-escena__titulo">Escena {escena.numeroEscena}</span>
                                            <button className="rs-escena__eliminar" onClick={() => eliminarEscena(indice)} type="button">✕</button>
                                        </div>
                                        <RSAvatarPicker
                                            avatares={avatares}
                                            value={escena.avatarId}
                                            onChange={(id) => actualizarEscena(indice, "avatarId", id)}
                                        />
                                        <textarea
                                            className="rs-textarea"
                                            rows={2}
                                            placeholder="¿Qué dirá el avatar en esta escena?"
                                            value={escena.texto}
                                            onChange={e => actualizarEscena(indice, "texto", e.target.value)}
                                        />
                                    </div>
                                ))}

                                <button className="rs-btn rs-btn--ghost" onClick={agregarEscena} type="button">+ Agregar escena</button>
                            </div>
                        </div>
                    )}

                    <div className="rs-field-row">
                        <div className="rs-field">
                            <label className="rs-field__label">Formato del video</label>
                            <select
                                className="rs-input"
                                value={aspectRatio}
                                onChange={e => setAspectRatio(e.target.value)}
                            >
                                <option value="9:16">9:16 (Vertical)</option>
                                <option value="1:1">1:1 (Cuadrado)</option>
                                <option value="16:9">16:9 (Horizontal)</option>
                            </select>
                        </div>
                        <div className="rs-field">
                            <label className="rs-field__label">Hora de publicación</label>
                            <input
                                className="rs-input"
                                type="time"
                                value={hora}
                                onChange={e => setHora(e.target.value)}
                            />
                        </div>
                    </div>

                    <div className="rs-field-row">
                        <div className="rs-field">
                            <label className="rs-field__label">Red Social</label>

                            <div style={{ display: "flex", gap: "20px", marginTop: "8px" }}>
                                <label>
                                    <input
                                        type="checkbox"
                                        checked={redSocial.includes("Facebook")}
                                        onChange={() => toggleRedSocial("Facebook")}
                                    />
                                    Facebook
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        checked={redSocial.includes("Instagram")}
                                        onChange={() => toggleRedSocial("Instagram")}
                                    />
                                    Instagram
                                </label>

                                <label>
                                    <input
                                        type="checkbox"
                                        checked={redSocial.includes("LinkedIn")}
                                        onChange={() => toggleRedSocial("LinkedIn")}
                                    />
                                    LinkedIn
                                </label>
                            </div>
                        </div>
                    </div>

                    <div className="rs-field">
                        <label className="rs-field__label">Días de publicación <span className="rs-field__hint">(selecciona o deselecciona días)</span></label>

                        <RSMiniCalendario dias={diasPub} />
                    </div>

                </div>

                <div className="rs-modal__footer">
                    {esEdicion && (
                        <button className="rs-btn rs-btn--danger" onClick={onEliminar} disabled={guardando}>
                            Eliminar
                        </button>
                    )}

                    {esEdicion && (
                        <button
                            className={`rs-btn ${cargar ? "rs-btn--warning" : "rs-btn--blue"}`}
                            onClick={() => setCargar(prev => !prev)}
                            disabled={guardando}
                            type="button"
                        >
                            {cargar ? "No cargar" : "✓ Cargar"}
                        </button>
                    )}

                    <button className="rs-btn rs-btn--ghost" onClick={onCerrar} disabled={guardando}>Cancelar</button>
                    <button className="rs-btn rs-btn--primary" onClick={onGuardar} disabled={guardando}>
                        {guardando ? "Guardando..." : esEdicion ? "Guardar cambios" : "Guardar"}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default RSReelsModal;